/**
 * Calcul du TRI (XIRR - Extended Internal Rate of Return) en JavaScript
 * Portage du code Python de l'ancienne application
 * 
 * CONVENTION DE SIGNE POUR XIRR :
 * - Flux NÉGATIFS = investissements (apports) → argent qui SORT
 * - Flux POSITIFS = sorties (retraits) OU valorisation finale → argent qui REVIENT
 * 
 * @param {Array<[string|Date, number]>} cashflows - Liste de tuples [date, montant]
 *   date peut être une string ISO ('YYYY-MM-DD') ou un objet Date
 *   montant: négatif pour apports, positif pour retraits/valorisation
 * @param {number} guess - Estimation initiale du taux (défaut: 0.1)
 * @param {number} tol - Tolérance pour la convergence (défaut: 1e-7)
 * @param {number} maxIter - Nombre maximum d'itérations (défaut: 300)
 * @returns {number|null} Taux de rendement interne (décimal), null si calcul impossible
 */
export function calculateXIRR(cashflows, guess = 0.1, tol = 1e-7, maxIter = 300) {
  if (!cashflows || cashflows.length < 2) {
    return null;
  }
  
  // Séparer les flux négatifs (apports) et positifs (retraits + valorisation)
  const fluxNegatifs = cashflows.filter(([_, v]) => v < 0).map(([_, v]) => v);
  const fluxPositifs = cashflows.filter(([_, v]) => v > 0).map(([_, v]) => v);
  
  // Vérifier la cohérence : on doit avoir au moins un apport et un flux positif
  if (fluxNegatifs.length === 0) {
    console.warn("TRI: Aucun flux négatif (apport) trouvé");
    return null;
  }
  
  if (fluxPositifs.length === 0) {
    console.warn("TRI: Aucun flux positif (valorisation/retrait) trouvé");
    return null;
  }
  
  // Calcul des totaux pour diagnostic et ajustement de tolérance
  const totalApports = Math.abs(fluxNegatifs.reduce((a, b) => a + b, 0));
  const totalSorties = fluxPositifs.reduce((a, b) => a + b, 0);
  
  // Ajuster la tolérance selon le ratio flux
  let adjustedTol = tol;
  const ratio = totalApports > 0 ? totalSorties / totalApports : 1;
  
  if (ratio > 10 || ratio < 0.1) {
    adjustedTol = 1e-5;
  }
  
  // Convertir les dates en objets Date et normaliser
  const flowsDt = cashflows.map(([dateItem, value]) => {
    let dateObj;
    if (typeof dateItem === 'string') {
      dateObj = new Date(dateItem);
    } else if (dateItem instanceof Date) {
      dateObj = dateItem;
    } else {
      throw new Error(`Format de date invalide: ${dateItem}`);
    }
    return [dateObj, parseFloat(value)];
  });
  
  // REGROUPER PAR DATE (comme TRI.PAIEMENT Excel : additionne les cashflows de même date)
  const cashflowsParDate = new Map();
  flowsDt.forEach(([dateDt, montant]) => {
    const dateStr = dateDt.toISOString().split('T')[0]; // Format YYYY-MM-DD
    const existing = cashflowsParDate.get(dateStr) || 0;
    cashflowsParDate.set(dateStr, existing + montant);
  });
  
  // Reconvertir en array de [Date, number] et trier par date
  const flowsDtGrouped = Array.from(cashflowsParDate.entries())
    .map(([dateStr, montant]) => [new Date(dateStr), montant])
    .sort((a, b) => a[0] - b[0]);
  
  // Fonction XNPV (Net Present Value)
  function xnpv(rate, flows) {
    if (!flows || flows.length === 0) {
      return 0;
    }
    
    const t0 = flows[0][0];
    let total = 0;
    
    for (const [t, v] of flows) {
      const years = (t - t0) / (365.0 * 24 * 60 * 60 * 1000); // Convertir millisecondes en années
      
      if (rate === -1) {
        // Éviter division par zéro
        return v > 0 ? Infinity : -Infinity;
      }
      
      const discount = Math.pow(1 + rate, years);
      if (discount === 0) {
        return Infinity;
      }
      
      total += v / discount;
    }
    
    return total;
  }
  
  // Fonction dérivée numérique de XNPV
  function xnpvDerivative(rate, flows, h = 1e-6) {
    const npvPlus = xnpv(rate + h, flows);
    const npvMinus = xnpv(rate - h, flows);
    
    if (!isFinite(npvPlus) || !isFinite(npvMinus)) {
      return 0;
    }
    
    return (npvPlus - npvMinus) / (2 * h);
  }
  
  // Méthode 1: Newton-Raphson avec plusieurs points de départ
  const guesses = [guess, 0.0, 0.05, 0.15, 0.25, -0.1, 0.5, 1.0];
  let bestRate = null;
  let bestNpv = Infinity;
  
  for (const initialGuess of guesses) {
    let r = initialGuess;
    
    for (let iteration = 0; iteration < maxIter; iteration++) {
      const f = xnpv(r, flowsDtGrouped);
      
      if (!isFinite(f)) {
        break;
      }
      
      if (Math.abs(f) < adjustedTol) {
        if (Math.abs(f) < bestNpv) {
          bestNpv = Math.abs(f);
          bestRate = r;
        }
        break;
      }
      
      const fp = xnpvDerivative(r, flowsDtGrouped);
      if (fp === 0 || !isFinite(fp)) {
        break;
      }
      
      let rNext = r - f / fp;
      
      // Limites pour éviter les valeurs aberrantes
      if (rNext <= -0.9999) {
        rNext = (r - 0.9999) / 2;
      }
      if (rNext > 50.0) {
        rNext = Math.min(rNext, 50.0);
      }
      if (!isFinite(rNext)) {
        break;
      }
      
      // Vérifier la convergence
      if (Math.abs(rNext - r) < 1e-10) {
        const npvAtNext = Math.abs(xnpv(rNext, flowsDtGrouped));
        if (npvAtNext < bestNpv) {
          bestNpv = npvAtNext;
          bestRate = rNext;
        }
        break;
      }
      
      r = rNext;
    }
  }
  
  if (bestRate !== null && bestNpv < adjustedTol) {
    // Validation du TRI calculé
    if (!(-0.99 <= bestRate && bestRate <= 99.99)) {
      console.warn(`TRI aberrant calculé: ${bestRate} (${bestRate * 100}%) pour ${cashflows.length} flux`);
      return null;
    }
    
    return bestRate;
  }
  
  // Méthode 2: Bisection avec recherche de changement de signe
  const rangesToTry = [
    [-0.99, 0.0],    // Pertes
    [0.0, 0.5],      // Gains modérés
    [0.5, 2.0],      // Gains importants
    [2.0, 10.0],     // Gains très élevés
    [-0.99, 10.0],   // Plage large
  ];
  
  for (const [lo, hi] of rangesToTry) {
    let npvLo = xnpv(lo, flowsDtGrouped);
    let npvHi = xnpv(hi, flowsDtGrouped);
    
    if (!isFinite(npvLo) || !isFinite(npvHi)) {
      continue;
    }
    
    // Vérifier s'il y a un changement de signe
    if (npvLo * npvHi < 0) {
      // Bisection
      let currentLo = lo;
      let currentHi = hi;
      
      for (let i = 0; i < 500; i++) {
        const mid = (currentLo + currentHi) / 2;
        const val = xnpv(mid, flowsDtGrouped);
        
        if (!isFinite(val)) {
          break;
        }
        
        if (Math.abs(val) < adjustedTol) {
          const result = mid;
          // Validation
          if (-0.99 <= result && result <= 99.99) {
            return result;
          }
          break;
        }
        
        const npvAtLo = xnpv(currentLo, flowsDtGrouped);
        if (npvAtLo * val < 0) {
          currentHi = mid;
        } else {
          currentLo = mid;
        }
        
        if (currentHi - currentLo < 1e-12) {
          const result = (currentLo + currentHi) / 2;
          // Validation
          if (-0.99 <= result && result <= 99.99) {
            return result;
          }
          break;
        }
      }
      
      // Retourner le meilleur résultat trouvé
      const result = (currentLo + currentHi) / 2;
      if (-0.99 <= result && result <= 99.99) {
        return result;
      }
    }
  }
  
  // Retourner le meilleur résultat de Newton-Raphson si disponible
  if (bestRate !== null && -0.99 <= bestRate && bestRate <= 99.99) {
    return bestRate;
  }
  
  return null;
}

